<script setup lang="ts">
import { watch } from 'vue';
import BackstageIcon from './BackstageIcon.vue';
import { PopUpSize } from '@/models/pop-up-size.enum';
import { PopUpType } from '@/models/pop-up-type.enum';
import { type NotifierButton } from '@/plugins/notifier';
import BackstageButton from './BackstageButton.vue';

interface Props {
  showOutsideCloseButton?: boolean;
  // we can remove string types once all components are on composition api which supports usage of enums on templates
  size?: PopUpSize | 'small' | 'medium' | 'large' | 'full';
  title?: string;
  body?: string;
  type?: PopUpType;
  buttons?: NotifierButton[];
  close?: () => void;
}

const emit = defineEmits<{
  close: [];
}>();

const props = withDefaults(defineProps<Props>(), {
  showOutsideCloseButton: true,
});

watch(
  () => props.showOutsideCloseButton,
  (newVal: boolean) => {
    if (newVal) {
      close();
    }
  },
);

function onCloseButton() {
  emit('close');

  if (props.close) {
    props.close();
  }
}

async function runAction(action?: () => Promise<void>) {
  if (action) {
    await action();
  }

  onCloseButton();
}
</script>
<template>
  <div ref="popup" tabindex="0" class="popup-cnt" v-on:keydown.esc="onCloseButton()">
    <div class="popup-shadow">
      <div v-if="showOutsideCloseButton">
        <button class="cancel-button" v-on:click="onCloseButton()">
          <BackstageIcon class="cancel-button close-icon" name="x-close" data-cy="popup-close-button-out">
          </BackstageIcon>
        </button>
      </div>
    </div>
    <div class="popup" data-cy="popup" :class="size">
      <div
        v-if="!showOutsideCloseButton"
        class="close"
        data-cy="popup-close-button-in"
        v-on:click="onCloseButton()"></div>
      <div class="icon-title" data-cy="popup-title">
        <BackstageIcon v-if="type" :name="type"></BackstageIcon>
      </div>
      <h3 v-html="title"></h3>
      <div class="popup-content" data-cy="popup-content">
        <span class="text-body" v-if="body">{{ body }}</span>
        <slot> </slot>
        <div v-if="buttons" class="buttons-container">
          <BackstageButton
            v-for="(button, index) in buttons"
            :key="index"
            :variant="button.variant"
            v-on:click="runAction(button.action)"
            data-cy="continue-button-cnt"
            data-cy-button="continue-button">
            {{ button.text }}
          </BackstageButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.popup-cnt {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;

  .popup-shadow {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1000;
    -webkit-backdrop-filter: blur(50px);
    backdrop-filter: blur(50px);
    background-color: rgba(0, 0, 0, 0.333);
    transition: opacity 0.3s;

    .cancel-button {
      cursor: pointer;
      position: absolute;
      background: none;
      border: none;

      .close-icon {
        padding: 0;
        border: none;
        position: fixed;
        width: 42px;
        height: 42px;
        top: 16px;
        right: 16px;
        background-color: var(--primary-bg-color);
        border-radius: 50%;
        opacity: 1;
      }
    }
  }

  .popup {
    z-index: 1001;
    background-color: var(--primary-bg-color);
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    padding: 44px;
    max-width: 90%;
    border-radius: 8px;

    h3 {
      padding: 0 0 32px;
      margin: 0;
      border-bottom: 1px var(--gray-color)-border solid;
      color: var(--primary-text-color);
      font-weight: 500;
      font-size: 1.625em;
      text-align: center;
    }

    .popup-content {
      overflow: auto;
      max-height: calc(100vh - 300px);

      .text-body {
        color: var(--primary-text-color);
      }

      .buttons-container {
        display: flex;
        justify-content: center;
        padding-top: 20px;
        gap: 20px;
      }
    }

    .icon-title {
      display: flex;
      justify-content: center;
      margin-bottom: 10px;

      i {
        width: 42px;
        height: 42px;
      }
    }

    .close {
      &::after,
      &::before {
        top: 5px;
        left: 15px;
      }
    }

    &.small {
      padding: 20px;
      max-width: 500px;

      h3 {
        font-weight: 500;
        font-size: 16px;
        line-height: 150%;
        padding: 0;
      }

      .close {
        display: none;
      }
    }

    &.medium {
      max-width: 720px;
    }

    &.large {
      min-width: 750px;
      max-width: 1200px;
    }

    &.full {
      max-width: unset;
      width: 100%;
      height: 100%;

      .popup-content {
        max-height: 100%;
      }
    }
  }

  @include breakpoint(small) {
    .popup {
      width: 98%;
      box-sizing: border-box;
      padding: 10px;
      font-size: 0.9em;

      .close {
        position: absolute;
        right: 2px;
        top: 2px;
        width: 22px;
        height: 22px;
        opacity: 0.7;
        cursor: pointer;

        &:after,
        &:before {
          position: absolute;
          left: 10px;
          top: 2px;
          height: 20px;
          width: 2px;
          background-color: var(--secondary-bg-color);
        }
      }
    }
  }

  &.popup-new {
    padding-top: 0;

    .popup {
      padding: 10px;

      h3 {
        display: none;
      }

      .close {
        top: 37px;
        right: 47px;
        width: 40px;
        height: 40px;

        @include breakpoint(xxlarge) {
          right: calc((100vw - 1327px) / 2);
        }

        &::after,
        &::before {
          display: none;
        }
      }

      .popup-content {
        padding: 2px;
      }
    }
  }
}
</style>
